import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { isObject } from 'lodash-es';
import { MenuItem, InlineSelect, Tooltip, Typography } from 'seagull';
import { activeAgreementCannotModifiyOccurrenceTooltip, activeAgreementCannotModifiyTriggerTooltip, makeInitialOneTimeInStages, serviceBillingOccurrenceOptions, serviceBillingStagesTermsOptions, serviceBillingTriggerOptions, yearlyServicesCanOnlyBeBilledManuallyTooltip } from "../../../business-logic/billingLogic";
import { BillingStagesSelect } from "../../ServiceCreation/ServiceBuilder/BillingStagesSelect/BillingStagesSelect";
import { useIoContext } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../../models";
import { isAwaitingApproval, isFromAmendment } from "../../../models/service/selectors";
import { RecurrencePeriodTypes } from "../../../models/service-billing/model";
import { price } from "../../../models/service-cost";
import styles from "./BillingInitiationControl.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function getOccurrenceLabel(occurrence) {
  if (!occurrence) {
    return 'once';
  }
  return serviceBillingModel.extendedRecurrencePeriodText(occurrence).toLowerCase();
}
function getTriggerLabel(option) {
  const autoLabel = 'automatically';
  const manualLabel = 'manually';
  const optionLabelMap = {
    approval: autoLabel,
    ongoing: autoLabel,
    manualVariable: manualLabel,
    manualFixed: manualLabel,
    oneTimeInStages: 'in stages'
  };
  return optionLabelMap[option];
}
function renderMenuItem(_ref) {
  let {
    value,
    text,
    subtext,
    testid,
    disabled,
    tooltipProps
  } = _ref;
  return /*#__PURE__*/_jsxs(MenuItem, {
    value: value,
    variant: "multiline",
    "data-testid": testid,
    style: {
      maxWidth: 330
    },
    disabled: disabled,
    tooltipProps: tooltipProps,
    children: [/*#__PURE__*/_jsx(Typography, {
      weight: "normal",
      children: text
    }), subtext && /*#__PURE__*/_jsx(Typography, {
      children: subtext
    })]
  }, value);
}
function matchOption(option, matcher) {
  if (option === 'manualFixed' || option === 'manualVariable') {
    matcher.manual();
  } else if (option === 'oneTimeInStages') {
    matcher.inStages();
  } else if (option === 'approval' || option === 'ongoing') {
    matcher.auto();
  }
}
function getOccurrenceValue(service) {
  const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
  const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
  if (oneTimeInStages) {
    return 'inStages';
  }
  if (!recurrencePeriod) {
    return 'onetime';
  }
  return recurrencePeriod;
}
function getTriggerValue(service) {
  const manualOption = () => serviceCostModel.isFixedPrice(service.cost) ? 'manualFixed' : 'manualVariable';
  return serviceBillingModel.accept(service.billing, {
    visitOneTimeManual: manualOption,
    visitOnApproval: () => 'approval',
    visitOneTimeInStages: () => 'oneTimeInStages',
    visitOngoing: () => 'ongoing',
    visitRepeatableManual: manualOption
  });
}
export default function BillingInitiationControl(props) {
  var _service$cost$discoun;
  const {
    testid,
    service,
    disabled,
    agreementData,
    isExistingService,
    isFreshlyAdded,
    onChange = () => {}
  } = props;
  const isDisabled = !!disabled;
  const disabledReason = isObject(disabled) ? disabled.reason : undefined;
  const featureFlags = useFeatureFlags();
  const {
    IN_STAGES_OCCURENCE,
    BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE
  } = featureFlags;
  const {
    getNow
  } = useIoContext();
  const now = useMemo(() => getNow(), []);
  const isFromAgreement = agreementData.isActiveAgreement();
  const occurrenceValue = getOccurrenceValue(service);
  const triggerValue = getTriggerValue(service);
  const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
  const isYearly = serviceBillingModel.recurrencePeriod(service.billing) === RecurrencePeriodTypes.Yearly;
  const shouldShowYearlyManualTooltip = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && !serviceBillingModel.isManual(service.billing);
  const termsOptions = serviceBillingStagesTermsOptions({
    now,
    agreementEffectiveDate: agreementData.effectiveDate,
    isFreshlyAddedToApprovedAgreement: isFromAgreement && !isExistingService
  });
  const occurrenceOptions = serviceBillingOccurrenceOptions({
    featureFlags
  });
  const triggerOptions = serviceBillingTriggerOptions({
    recurrencePeriod: serviceBillingModel.recurrencePeriod(service.billing),
    pricingType: service.cost.pricingType,
    billingTrigger: service.billing.trigger,
    isFromAgreement,
    isExistingService,
    isFreshlyAdded,
    featureFlags
  });
  function handleOccurrenceChange(event) {
    const newOccurrence = event.target.value;
    const currentRecurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
    const isRecurring = !!currentRecurrencePeriod;
    if (newOccurrence === 'onetime') {
      if (isRecurring || oneTimeInStages) {
        onChange(serviceModel.setOneTimeManual(service));
      }
    } else if (newOccurrence === 'inStages') {
      onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
    } else {
      const newRecurrencePeriod = newOccurrence;
      serviceBillingModel.accept(service.billing, {
        visitOneTimeManual: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {})));
        },
        visitOnApproval: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
            isAwaitingApproval: isAwaitingApproval(service.status)
          })));
        },
        visitOneTimeInStages: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {})));
        },
        visitOngoing: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
            isAwaitingApproval: isAwaitingApproval(service.status),
            billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
            billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
            billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
            isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
            maxCharges: serviceBillingModel.maxCharges(service.billing)
          })));
        },
        visitRepeatableManual: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {
            billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
            billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
            billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
            isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
            maxCharges: serviceBillingModel.maxCharges(service.billing)
          })));
        }
      });
    }
  }
  function handleTriggerChange(event) {
    const option = event.target.value;
    const setInStages = () => onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
    const setOnApproval = () => onChange(serviceModel.setOnApproval(service));
    const setOneTimeManual = () => onChange(serviceModel.setOneTimeManual(service));
    const doNothing = () => {};
    serviceBillingModel.accept(service.billing, {
      visitOneTimeManual: () => matchOption(option, {
        manual: doNothing,
        auto: setOnApproval,
        inStages: setInStages
      }),
      visitOnApproval: () => matchOption(option, {
        manual: setOneTimeManual,
        auto: doNothing,
        inStages: setInStages
      }),
      visitOneTimeInStages: () => matchOption(option, {
        manual: setOneTimeManual,
        auto: setOnApproval,
        inStages: doNothing
      }),
      visitRepeatableManual: () => matchOption(option, {
        manual: doNothing,
        auto: () => onChange(serviceModel.setOngoing(service)),
        inStages: doNothing
      }),
      visitOngoing: () => matchOption(option, {
        manual: () => onChange(serviceModel.setRepeatableManual(service)),
        auto: doNothing,
        inStages: doNothing
      })
    });
  }
  function handleTermsChange(event) {
    if (!oneTimeInStages) return;
    const terms = event.target.value;
    onChange(serviceModel.setOneTimeInStages(service, oneTimeInStages.setTerms(terms)));
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": "".concat(testid, "-billing-initiation-control"),
    children: [/*#__PURE__*/_jsxs(Typography, {
      children: ["Billing is initiated", ' ', /*#__PURE__*/_jsxs("div", {
        className: styles.dropdowns,
        children: [triggerOptions.length < 2 && !BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE || IN_STAGES_OCCURENCE && !BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE && oneTimeInStages ? /*#__PURE__*/_jsx(Typography, {
          as: "span",
          weight: "normal",
          children: getTriggerLabel(triggerValue)
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE && /*#__PURE__*/_jsx(Tooltip, {
            placement: "top",
            "data-testid": "".concat(testid, "-occurrence-tooltip"),
            title: disabledReason === 'existingServiceInAgreement' ? activeAgreementCannotModifiyOccurrenceTooltip : '',
            children: /*#__PURE__*/_jsx(InlineSelect, {
              skin: "primary",
              value: occurrenceValue,
              "data-testid": "".concat(testid, "-occurrence"),
              disabled: isDisabled,
              onChange: handleOccurrenceChange,
              menuMaxHeight: 200,
              SelectProps: {
                style: {
                  width: 'auto'
                },
                renderValue: value => getOccurrenceLabel(value)
              },
              children: occurrenceOptions.map(_ref2 => {
                let {
                  value,
                  text
                } = _ref2;
                return renderMenuItem({
                  value,
                  text,
                  testid: "".concat(testid, "-occurrence-option-").concat(value)
                });
              })
            })
          }), !(IN_STAGES_OCCURENCE && BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE && oneTimeInStages) && /*#__PURE__*/_jsx(Tooltip, {
            placement: "top",
            "data-testid": "".concat(testid, "-trigger-tooltip"),
            title: disabledReason === 'existingServiceInAgreement' ? activeAgreementCannotModifiyTriggerTooltip : shouldShowYearlyManualTooltip ? yearlyServicesCanOnlyBeBilledManuallyTooltip : '',
            children: /*#__PURE__*/_jsx(InlineSelect, {
              skin: "primary",
              value: triggerValue,
              "data-testid": "".concat(testid, "-trigger"),
              disabled: isDisabled,
              error: shouldShowYearlyManualTooltip,
              onChange: handleTriggerChange,
              menuMaxHeight: 500,
              SelectProps: {
                renderValue: value => getTriggerLabel(value)
              },
              children: triggerOptions.map(_ref3 => {
                let {
                  value,
                  text,
                  subtext
                } = _ref3;
                const isAutomaticOption = value === 'approval' || value === 'ongoing';
                const shouldDisableAutomaticOption = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && isAutomaticOption;
                return renderMenuItem({
                  value,
                  text,
                  subtext,
                  testid: "".concat(testid, "-trigger-option-").concat(value),
                  disabled: shouldDisableAutomaticOption,
                  tooltipProps: shouldDisableAutomaticOption ? {
                    title: yearlyServicesCanOnlyBeBilledManuallyTooltip
                  } : undefined
                });
              })
            })
          })]
        }), oneTimeInStages && /*#__PURE__*/_jsx(InlineSelect, {
          skin: "primary",
          value: oneTimeInStages.terms(),
          "data-testid": "".concat(testid, "-terms"),
          disabled: isDisabled,
          onChange: handleTermsChange,
          SelectProps: {
            renderValue: value => {
              const selected = termsOptions.find(opt => opt.value === value);
              return selected ? selected.text : '';
            }
          },
          children: termsOptions.map(_ref4 => {
            let {
              value,
              text,
              subtext
            } = _ref4;
            return renderMenuItem({
              value,
              text,
              subtext,
              testid: "".concat(testid, "-terms-option-").concat(value)
            });
          })
        })]
      })]
    }), oneTimeInStages && /*#__PURE__*/_jsx(BillingStagesSelect, {
      discount: ((_service$cost$discoun = service.cost.discount) === null || _service$cost$discoun === void 0 ? void 0 : _service$cost$discoun.amount()) || new Decimal(0),
      totalRate: price(service.cost),
      effectiveDate: agreementData.effectiveDate,
      oneTimeInStagesBilling: oneTimeInStages,
      setOneTimeInStagesBilling: oneTimeInStagesBilling => {
        onChange(serviceModel.setOneTimeInStages(service, oneTimeInStagesBilling));
      },
      disabledReason: disabledReason,
      isAmendment: isFromAmendment(service)
    })]
  });
}